import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiArrowLeft } from 'react-icons/fi';
import { postsAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const AdminPanel = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasRole('administrador')) {
      navigate('/estudios');
      return;
    }
    cargarPosts();
  }, []);

  const cargarPosts = async () => {
    try {
      setLoading(true);
      // Cargar todos los posts (publicados y no publicados)
      const response = await postsAPI.getPublicados({ limit: 100, publicado: 'todos' });
      setPosts(response.data.data.posts || []);
    } catch (error) {
      console.error('Error al cargar posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const eliminarPost = async (id, titulo) => {
    if (!window.confirm(`¿Estás seguro de eliminar el post "${titulo}"?`)) {
      return;
    }

    try {
      await postsAPI.eliminar(id);
      cargarPosts();
    } catch (error) {
      console.error('Error al eliminar post:', error);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Link
                to="/estudios"
                className="flex items-center text-gray-600 hover:text-gray-900 mb-2"
              >
                <FiArrowLeft className="mr-2" />
                Volver a Estudios
              </Link>
              <h1 className="text-3xl font-bold text-gray-900">
                Panel de Administración
              </h1>
              <p className="text-gray-600 mt-1">
                Gestiona los estudios bíblicos
              </p>
            </div>
            <Link
              to="/admin/posts/nuevo"
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              <FiPlus className="mr-2" />
              Nuevo Post
            </Link>
          </div>
        </div>
      </div>

      {/* Tabla de posts */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Título
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visualizaciones
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {posts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    No hay posts creados. Crea tu primer post.
                  </td>
                </tr>
              ) : (
                posts.map((post) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {post.imagen && (
                          <img
                            src={post.imagen}
                            alt=""
                            className="h-10 w-10 rounded object-cover mr-3"
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {post.titulo}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-md">
                            {post.descripcion}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          post.publicado
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {post.publicado ? 'Publicado' : 'Borrador'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <FiEye className="mr-1" />
                        {post.visualizaciones || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(post.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Link
                          to={`/estudios/${post.slug}`}
                          className="text-gray-600 hover:text-gray-900"
                          title="Ver"
                        >
                          <FiEye size={18} />
                        </Link>
                        <Link
                          to={`/admin/posts/editar/${post.id}`}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Editar"
                        >
                          <FiEdit2 size={18} />
                        </Link>
                        <button
                          onClick={() => eliminarPost(post.id, post.titulo)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;

